import { z } from "zod";
import type { LlmClient, LlmRequest, ToolDesign } from "@keystone/core";
import { canonicalSha256 } from "@keystone/core";
import { KNOWLEDGE_BASE, formatKnowledgeBaseForPrompt, getKbClass } from "./knowledge-base.js";

/**
 * Agentic poisoning scanner. Where `scanForPoisoning` is a list of static
 * regex rules (cheap, deterministic, in-process), this scanner consults an
 * LLM with the structured knowledge base as context and asks: "for each
 * tool, which KB classes (if any) apply, with what evidence?"
 *
 * Output shape matches the static scanner's `PoisoningFinding` so downstream
 * consumers (eval, redteam differential, scorecard, attestation) don't need
 * to branch. Each agentic finding carries `source: "agentic"` and `classId`
 * for auditability.
 *
 * Determinism: pass a `RecordedLlmClient` for CI / browser; pass an
 * `AnthropicLlmClient` only when re-recording. Live calls outside RECORD mode
 * are a TDD failure.
 */

export const AgenticFindingSchema = z.object({
  toolName: z.string(),
  classId: z.string(),
  severity: z.enum(["low", "medium", "high", "blocker"]),
  rationale: z.string(),
  /** Verbatim quote from the tool's text that triggered the finding. */
  evidence: z.string(),
  source: z.literal("agentic")
});
export type AgenticFinding = z.infer<typeof AgenticFindingSchema>;

export interface AgenticScanInput {
  design: ToolDesign;
  llm: LlmClient;
  /** Override model id. Defaults to the cheapest capable Sonnet. */
  model?: string;
}

const MODEL_SCAN_INSTRUCTIONS = [
  "You are Keystone's security scanner. You are given a structured knowledge base of",
  "MCP tool-poisoning attack classes, then a list of tools (name + description). For",
  "each tool, identify which knowledge-base classes (if any) apply and quote the",
  "exact substring that triggered the finding.",
  "",
  "Rules:",
  "  1. Cite by classId (KB001, KB002, ...). Do not invent new ids.",
  "  2. Use the class's default severity unless the evidence clearly warrants escalating.",
  "  3. Each evidence string MUST be a verbatim substring of the tool's name or description.",
  "  4. If a tool is clean, return an empty findings array for it — do NOT fabricate findings.",
  "  5. Be calibrated: KB009 (context-window bloat) should not fire on descriptions <800 chars.",
  "     KB010 (homoglyph) only fires when you can name the sibling tool.",
  "",
  "Return ONLY a JSON object matching this shape (no markdown fences, no prose):",
  '  { "perTool": [ { "toolName": "...", "findings": [ { "classId": "KB001", "severity": "high", "rationale": "...", "evidence": "..." } ] } ] }'
].join("\n");

export const ScannerOutputSchema = z.object({
  perTool: z.array(
    z.object({
      toolName: z.string(),
      findings: z.array(
        z.object({
          classId: z.string(),
          severity: z.enum(["low", "medium", "high", "blocker"]),
          rationale: z.string(),
          evidence: z.string()
        })
      )
    })
  )
});

/** Build the LlmRequest envelope (exported so tests can compute the canonical hash). */
export function buildAgenticScanRequest(design: ToolDesign, model: string): LlmRequest {
  const surface = design.tools.map((t) => ({
    name: t.name,
    title: t.title,
    description: t.description,
    annotations: {
      readOnlyHint: t.annotations.readOnlyHint,
      destructiveHint: t.annotations.destructiveHint,
      idempotentHint: t.annotations.idempotentHint
    }
  }));
  const sysPrompt = [
    MODEL_SCAN_INSTRUCTIONS,
    "",
    "=== KNOWLEDGE BASE ===",
    formatKnowledgeBaseForPrompt()
  ].join("\n");
  return {
    model,
    temperature: 0,
    maxTokens: 4096,
    messages: [
      { role: "system", content: sysPrompt },
      { role: "user", content: "Tools to scan (JSON):\n" + JSON.stringify(surface, null, 2) }
    ]
  };
}

export async function runAgenticScan(input: AgenticScanInput): Promise<AgenticFinding[]> {
  const model = input.model ?? "claude-sonnet-4-6";
  const req = buildAgenticScanRequest(input.design, model);
  const res = await input.llm.complete(req);
  let parsed: z.infer<typeof ScannerOutputSchema>;
  try {
    parsed = ScannerOutputSchema.parse(JSON.parse(res.text));
  } catch (e) {
    throw new Error(
      "agentic-scanner: model output did not match ScannerOutputSchema. " +
        "First 200 chars: " +
        res.text.slice(0, 200) +
        " — parse error: " +
        (e as Error).message
    );
  }
  const out: AgenticFinding[] = [];
  for (const perTool of parsed.perTool) {
    for (const f of perTool.findings) {
      const kbClass = getKbClass(f.classId);
      if (!kbClass) continue; // model invented an id — drop it (rule 1)
      // Confirm evidence is a verbatim substring of the tool's name or description.
      const tool = input.design.tools.find((t) => t.name === perTool.toolName);
      if (!tool) continue; // model named a tool we didn't pass
      const haystack = `${tool.name}\n${tool.title}\n${tool.description}`;
      if (!haystack.includes(f.evidence) && f.evidence.length > 0) continue; // hallucinated quote
      out.push(
        AgenticFindingSchema.parse({
          toolName: perTool.toolName,
          classId: f.classId,
          severity: f.severity,
          rationale: f.rationale,
          evidence: f.evidence,
          source: "agentic"
        })
      );
    }
  }
  return out;
}

/** Hash of the canonical scan request — used to key transcripts in the fixture file. */
export function agenticScanRequestHash(design: ToolDesign, model = "claude-sonnet-4-6"): string {
  return canonicalSha256(buildAgenticScanRequest(design, model));
}

/** Re-export the KB so callers don't need to import two modules. */
export { KNOWLEDGE_BASE };
